import { useRef } from 'react';
import { Button } from 'react-bootstrap';
import UploadFileProgress from '../FileNavigator/UploadFileProgress';
import spinnerTitle from 'components/others/spinnerTitle';
import FilesCloudUploadItem from './FilesCloudUploadItem';
import SolarIcon from 'components/icons/SolarIcon';

interface FilesCloudUploadProps {
    disabled?: boolean;
    progress: number | boolean;
    files: File[];
    setFiles: React.Dispatch<React.SetStateAction<File[]>>;
    label: string;
    buttonLabel: string;
    uploadingLabel: string;
    showCloudflareLogo?: boolean;
}

const FilesCloudUpload = ({
    disabled = false,
    progress,
    files,
    setFiles,
    label,
    buttonLabel,
    uploadingLabel,
    showCloudflareLogo = true,
}: FilesCloudUploadProps) => {
    const refInputFile = useRef<HTMLInputElement | null>(null);

    const handleChangeFile = () => {
        const selectedFiles = refInputFile?.current?.files;
        if (!selectedFiles?.length) return;
        const selectedList = Array.from(selectedFiles);
        setFiles((prev) => [...prev, ...selectedList]);
    };

    const handleDelete = (index: number) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="mb-3">
            <input type="file" ref={refInputFile} onChange={handleChangeFile} className="d-none" multiple />

            <label className="form-label text-muted d-flex justify-content-between align-items-center mb-1">
                <span>{label}</span>
                {showCloudflareLogo && (
                    <img
                        src="/imgs/cloudflare.png"
                        alt="Cloudflare"
                        style={{ height: '24px', width: 'auto', objectFit: 'contain', opacity: 0.9 }}
                    />
                )}
            </label>

            <div className="form-control">
                {files.length > 0 && (
                    <ul className="list-inline mb-3">
                        {files.map((file, index) => (
                            <FilesCloudUploadItem
                                key={`${file.name}-${file.size}-${index}`}
                                file={file}
                                fileIndex={index}
                                disabled={disabled}
                                onDelete={handleDelete}
                            />
                        ))}
                    </ul>
                )}

                <Button
                    variant="light-secondary"
                    size="sm"
                    className="d-flex align-items-center"
                    disabled={disabled || progress !== false}
                    onClick={() => {
                        if (!refInputFile.current) return;
                        refInputFile.current.value = '';
                        refInputFile.current.click();
                    }}
                >
                    {spinnerTitle(
                        <>
                            <SolarIcon name="cloud-upload" className="fs-16 me-2" />
                            {buttonLabel}
                        </>,
                        progress !== false,
                        uploadingLabel,
                    )}
                </Button>

                {progress !== false && files.length > 0 && (
                    <div className="pt-2">
                        <UploadFileProgress progress={Number(progress)} />
                    </div>
                )}
            </div>
        </div>
    );
};

export default FilesCloudUpload;
